import { env } from '../config/env';
import { authService as mockAuthService, type Session } from './auth.mock';
import { apiAuthService } from './auth.api';

/**
 * The adapter selector.
 *
 * One decision, made once, from configuration. The alternative — a condition
 * inside each service, or worse inside each screen — is how an application ends
 * up half-connected, showing a real employee directory beside fictional
 * attendance, with nothing on screen to say so.
 *
 * `restore()` widens to a promise here because the API adapter has to ask the
 * server. The mock's synchronous answer still satisfies it, so the caller
 * awaits either without caring which it got.
 */

export type { Session };

/** What the sign-in page offers; the server's answer, or the prototype's. */
export interface SignInOptions {
  passwordLogin: boolean;
  magicLink: boolean;
  google: boolean;
}

export interface AuthService {
  restore(): Session | null | Promise<Session | null>;
  signIn(email: string, password: string): Promise<Session>;
  signInOptions(): Promise<SignInOptions>;
  /** Resolves with the server's sentence, the same whether or not the address is registered. */
  requestSignInLink(email: string): Promise<string>;
  /** Finish signing in with the token from an emailed link (/auth/magic). */
  signInWithLink(token: string): Promise<Session>;
  /** Where the browser goes to sign in with Google. */
  googleSignInUrl(): string;
  signOut(session: Session | null): Promise<void>;
  switchTo(email: string): Promise<Session>;
  demoAccounts(): { name: string; email: string; roles: string[] }[];
}

const apiAdapter: AuthService = {
  restore: () => apiAuthService.restore(),
  signIn: (email, password) => apiAuthService.signIn(email, password),
  signInOptions: () => apiAuthService.signInOptions(),
  requestSignInLink: (email) => apiAuthService.requestSignInLink(email),
  signInWithLink: (token) => apiAuthService.signInWithLink(token),
  googleSignInUrl: () => apiAuthService.googleSignInUrl(),
  signOut: () => apiAuthService.signOut(),
  switchTo: (email) => apiAuthService.switchTo(email),
  demoAccounts: () => apiAuthService.demoAccounts(),
};

/*
 * The prototype has no mailbox and no Google: it signs in with the shared
 * demonstration password, so that is all its sign-in page offers.
 */
const mockAdapter: AuthService = {
  restore: () => mockAuthService.restore(),
  signIn: (email, password) => mockAuthService.signIn(email, password),
  signOut: (session) => mockAuthService.signOut(session),
  switchTo: (email) => mockAuthService.switchTo(email),
  demoAccounts: () => mockAuthService.demoAccounts(),
  signInOptions: async () => ({ passwordLogin: true, magicLink: false, google: false }),
  requestSignInLink: async () => {
    throw new Error('Emailed sign-in links need the real API.');
  },
  signInWithLink: async () => {
    throw new Error('Emailed sign-in links need the real API.');
  },
  googleSignInUrl: () => '/',
};

export const authService: AuthService = env().dataSource === 'api' ? apiAdapter : mockAdapter;
